/* assets/multiupload-ga-tracking.js
 *
 * GA4 interaction tracking for the DTF "by size" Multiupload modal (the modal
 * that opens when a customer uploads 2+ files on a by-size PDP). Goal: separate
 * "proceeded to checkout" from "abandoned" and capture the friction signals in
 * between, to help close the upload -> order leak.
 *
 * Dispatch mirrors the existing DTF-page convention (assets/dtf-upsell-popup.js
 * fireEvent / assets/dtf-cta-tracking.js): gtag('event', name, params), guarded
 * by `typeof gtag`. Every event carries upload_count; per-upload events also
 * carry upload_index (the 1-based block-no of the upload block). environment +
 * device_type are attached to match the avatar / CTA tracking convention
 * (assets/avatar-tracking.js, assets/dtf-cta-tracking.js) -- lets us filter
 * sandbox/staging test traffic out of the funnel.
 *
 * WHY THIS SHAPE (see snippets/__multiuploadSnippets.liquid + assets/bySize_popular.js):
 *  - The modal root <multi-upload-wrapper> is cloned into <body> on demand and
 *    removed on dismiss/proceed. So OPEN/CLOSE are detected with a MutationObserver
 *    on <body>, and every control listener is document-level + CAPTURE phase.
 *  - Capture phase is deliberate: the theme's own delegated handlers call
 *    e.stopImmediatePropagation() (bySize_popular.js:482,1238,1330). Capture-phase
 *    listeners run before those bubble-phase handlers, so they are never swallowed.
 *    (Same approach as assets/portal-nav-tracking.js.)
 *  - Native capture listeners also naturally IGNORE the theme's programmatic jQuery
 *    .trigger()/.prop() calls (tab/toggle/size restores), which would otherwise be
 *    false fires -- jQuery .trigger() does not dispatch a native DOM event.
 *  - EXCEPTION: the width/height +/- steppers update the field then jQuery
 *    .trigger('keyup') (bySize_popular.js:502,526) -- not a native event -- so the
 *    stepper BUTTONS are tracked via their own click; typed edits via native 'input'.
 *  - Upload blocks are <uploaded-files-block block-no="N"> (1-based, bySize_popular.js:3245).
 *  - Single-upload PDP controls reuse the same classes but live under <upload-controls>,
 *    not <multi-upload-wrapper>, so every handler is scoped with closest('multi-upload-wrapper').
 */
(function () {
  'use strict';

  if (window.__multiuploadGATracking) return;
  window.__multiuploadGATracking = true;

  var DEBOUNCE_MS = 300;
  var OPEN_SETTLE_MS = 250; // let the initial upload blocks append before reading upload_count

  /* ----------------------------- dispatch ----------------------------- */

  function environment() {
    return (window.Shopify && Shopify.theme && Shopify.theme.role === 'main')
      ? 'production'
      : 'staging';
  }

  function deviceType() {
    return window.innerWidth <= 767 ? 'mobile' : 'desktop';
  }

  // dtf vs uv builder (uv-dtf-stickers-decals-by-size = suffix uv-by-size-middle-scroll).
  // The AI Edit Tools dropdown renders on both; product_type keeps them separable in GA4.
  function productType() {
    return /(^|\s)template-product-uv-by-size-middle-scroll(\s|$)/.test(document.body.className) ? 'uv' : 'dtf';
  }

  // AI Edit Tools dropdown (nt-dtf-3col.js): data-nt-aied value -> GA tool param.
  var AIED_TOOL_MAP = { edit: 'edit_with_prompt', recolor: 'recolor_image', effects: 'image_effects' };

  function getUploadCount() {
    return document.querySelectorAll('multi-upload uploaded-files-block').length;
  }

  function fireGAEvent(name, params) {
    if (typeof gtag === 'undefined') return;
    params = params || {};
    params.upload_count = getUploadCount();
    params.environment = environment();
    params.device_type = deviceType();
    gtag('event', name, params);
  }

  // 1-based upload-block index containing `el`, or undefined if not inside a block.
  function getUploadIndex(el) {
    var block = el && el.closest ? el.closest('uploaded-files-block') : null;
    if (!block) return undefined;
    var n = parseInt(block.getAttribute('block-no'), 10);
    return isNaN(n) ? undefined : n;
  }

  /* ---------------------- debounced + de-duped commits ----------------------
   * Size (width/height) and qty fire on COMMIT, not per keystroke / per +/- click:
   * a change schedules a 300ms-idle commit; a blur/change flushes it immediately;
   * a per-(block,field) "last committed value" guard collapses both paths into a
   * single event per actually-changed value. */

  var commitTimers = {};
  var lastCommitted = {};

  function resetCommitState() {
    commitTimers = {};
    lastCommitted = {};
  }

  function scheduleCommit(key, fn, field) {
    if (commitTimers[key]) clearTimeout(commitTimers[key]);
    commitTimers[key] = setTimeout(function () {
      commitTimers[key] = null;
      fn(field);
    }, DEBOUNCE_MS);
  }

  function flushCommit(key, fn, field) {
    if (commitTimers[key]) {
      clearTimeout(commitTimers[key]);
      commitTimers[key] = null;
    }
    fn(field);
  }

  function fieldDimension(field) {
    var item = field.closest ? field.closest('.widthHeight__item') : null;
    return item ? item.getAttribute('by') : null; // 'width' | 'height'
  }

  function commitSize(field) {
    var idx = getUploadIndex(field);
    var dim = fieldDimension(field);
    if (idx === undefined || !dim) return;
    var num = parseFloat((field.value || '').trim());
    if (isNaN(num)) return;
    var key = 'sz:' + idx + ':' + dim;
    if (lastCommitted[key] === String(num)) return;
    lastCommitted[key] = String(num);
    fireGAEvent('multiupload_custom_size_edit', { dimension: dim, size_value: num, upload_index: idx });
  }

  function commitQty(field) {
    var idx = getUploadIndex(field);
    if (idx === undefined) return;
    var num = parseInt((field.value || '').trim(), 10);
    if (isNaN(num)) return;
    var key = 'qty:' + idx;
    if (lastCommitted[key] === String(num)) return;
    lastCommitted[key] = String(num);
    fireGAEvent('multiupload_qty_change', { qty: num, upload_index: idx });
  }

  function sizeKey(field) {
    var idx = getUploadIndex(field);
    var dim = fieldDimension(field);
    return (idx === undefined || !dim) ? null : 'sz:' + idx + ':' + dim;
  }
  function qtyKeyFor(field) {
    var idx = getUploadIndex(field);
    return idx === undefined ? null : 'qty:' + idx;
  }

  /* ----------------------- open / proceed / abandon ----------------------- */

  var openFired = false;
  var proceeded = false;
  var openTimer = null;

  function onOpen() {
    if (openTimer || openFired) return; // a settle is pending, or already open
    openTimer = setTimeout(function () {
      openTimer = null;
      if (openFired) return;
      if (!document.querySelector('multi-upload-wrapper')) return; // closed before settle
      openFired = true;
      proceeded = false;
      resetCommitState();
      fireGAEvent('multiupload_modal_open', {});
    }, OPEN_SETTLE_MS);
  }

  function onClose() {
    if (openTimer) { clearTimeout(openTimer); openTimer = null; }
    if (document.querySelector('multi-upload-wrapper')) return; // re-render, not a real close
    if (openFired && !proceeded) fireGAEvent('multiupload_abandon', {});
    openFired = false;
    proceeded = false;
    resetCommitState();
  }

  /* MutationObserver: <multi-upload-wrapper> added => open; removed => close.
   * activateMultiupload() removes-then-appends synchronously (bySize_popular.js:2967-2969),
   * so a rebuild lands in ONE mutation batch -> treat "added this batch" as open/keep-open
   * and skip the close, so a rebuild never reads as abandon + re-open. */
  function nodeTouchesWrapper(node) {
    if (!node || node.nodeType !== 1) return false;
    if (node.matches && node.matches('multi-upload-wrapper')) return true;
    return !!(node.querySelector && node.querySelector('multi-upload-wrapper'));
  }

  var observer = new MutationObserver(function (mutations) {
    var added = false, removed = false;
    for (var i = 0; i < mutations.length; i++) {
      var m = mutations[i];
      var an = m.addedNodes, rn = m.removedNodes, j;
      for (j = 0; j < an.length; j++) if (nodeTouchesWrapper(an[j])) { added = true; break; }
      for (j = 0; j < rn.length; j++) if (nodeTouchesWrapper(rn[j])) { removed = true; break; }
    }
    if (added) onOpen();
    else if (removed) onClose();
  });

  function startObserver() {
    if (document.body) observer.observe(document.body, { childList: true });
  }
  if (document.body) startObserver();
  else document.addEventListener('DOMContentLoaded', startObserver);

  /* ------------------------------ clicks ------------------------------ */

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;

    // Proceed to Checkout (modal-only class; single-upload uses .addToCartGroupItems)
    if (t.closest('proceed-to-checkout .addToCartGroupItemsMultiple')) {
      proceeded = true;
      fireGAEvent('multiupload_proceed_checkout', {});
      return;
    }

    // AI Edit Tools dropdown (nt-dtf-3col.js) on the builder-tile hosts inside
    // <multi-upload-wrapper>. ⚠ These run BEFORE the wrapper guard below because the open
    // MENU portals to <body>, so the clicked .nt-aied-item is no longer inside the
    // wrapper (the guard would drop it). The TRIGGER stays in place and the open host
    // stays in place — scope + upload_index off the HOST, never the (portaled) item.
    // This capture listener runs before nt-dtf-3col's BUBBLE closeAll(), so
    // .nt-aied-open is still set at item-click time. The single-upload rail/flat host is
    // in <upload-controls> (not the wrapper) and is handled by dtf-pdp-tracking.js —
    // mutually exclusive, so exactly one tracker fires per click.
    var aiedTrg = t.closest('.nt-aied-trigger');
    if (aiedTrg) {
      var trgHost = aiedTrg.closest('.nt-aied-host');
      if (trgHost && trgHost.closest('multi-upload-wrapper') && !trgHost.classList.contains('nt-aied-open')) {
        fireGAEvent('multiupload_ai_edit_tools_open', { product_type: productType(), upload_index: getUploadIndex(trgHost) });
      }
      return;
    }
    var aiedItem = t.closest('.nt-aied-item');
    if (aiedItem) {
      var aiedHost = document.querySelector('.nt-aied-host.nt-aied-open');
      if (aiedHost && aiedHost.closest('multi-upload-wrapper')) {
        var tool = AIED_TOOL_MAP[aiedItem.getAttribute('data-nt-aied')];
        if (tool) fireGAEvent('multiupload_ai_edit_tool_click', { tool: tool, product_type: productType(), upload_index: getUploadIndex(aiedHost) });
      }
      return;
    }

    // Everything else must be inside the multiupload modal.
    if (!t.closest('multi-upload-wrapper')) return;

    // Delete this upload (the "Delete" link and the preview trash icon both remove the block)
    var del = t.closest('item-no .itemNo__action a, preview-box [removefile]');
    if (del) {
      fireGAEvent('multiupload_delete_upload', { upload_index: getUploadIndex(del) });
      return;
    }

    // Edit with AI
    var ai = t.closest('.edit-with-ai');
    if (ai) {
      fireGAEvent('multiupload_edit_ai_click', { upload_index: getUploadIndex(ai) });
      return;
    }

    // Add another size of this design
    var add = t.closest('.addmore_size');
    if (add) {
      fireGAEvent('multiupload_add_size_click', { upload_index: getUploadIndex(add) });
      return;
    }

    // Width/height +/- steppers. The theme updates the field value in its own
    // (later) bubble handler, so schedule a debounced commit -- by the time it
    // runs the value is settled; de-dupe collapses a burst of clicks to one event.
    var step = t.closest('.widthHeight__value-plus, .widthHeight__value-minus');
    if (step) {
      var wrap = step.closest('.widthHeight__item_inputWrapper');
      var field = wrap ? wrap.querySelector('.widthHeight__value') : null;
      if (field) {
        var k = sizeKey(field);
        if (k) scheduleCommit(k, commitSize, field);
      }
      return;
    }
  }, true);

  /* ------------------------------ change ------------------------------ */

  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t || !t.closest || !t.closest('multi-upload-wrapper')) return;
    var cl = t.classList;
    if (!cl) return;

    // Custom Size <-> Popular Size tab toggle (hidden radios)
    if (cl.contains('tabOptionName')) {
      if (t.checked) {
        fireGAEvent('multiupload_size_tab_switch', { tab: t.value, upload_index: getUploadIndex(t) });
      }
      return;
    }

    // Popular size <select> -- value carries '^' in place of '"' (Liquid-escaped); restore it.
    if (cl.contains('popularSizes')) {
      var opt = (t.options && t.selectedIndex >= 0) ? t.options[t.selectedIndex] : null;
      var raw = opt ? (opt.value || '') : (t.value || '');
      if (raw) {
        fireGAEvent('multiupload_popular_size_select', {
          size: raw.replace(/\^/g, '"').trim(),
          upload_index: getUploadIndex(t)
        });
      }
      return;
    }

    // Remove Background / Upscale toggles (both share .toggleOption; parent .switch distinguishes)
    if (cl.contains('toggleOption')) {
      var sw = t.closest('.switch');
      var state = t.checked ? 'on' : 'off';
      if (sw && sw.classList.contains('bgRemover')) {
        fireGAEvent('multiupload_remove_bg_toggle', { state: state, upload_index: getUploadIndex(t) });
      } else if (sw && sw.classList.contains('superRes')) {
        fireGAEvent('multiupload_upscale_toggle', { state: state, upload_index: getUploadIndex(t) });
      }
      return;
    }

    // qty / width / height commit on blur (change fires on commit for these inputs)
    if (cl.contains('customQtyFile__qty')) {
      var qk = qtyKeyFor(t);
      if (qk) flushCommit(qk, commitQty, t);
      return;
    }
    if (cl.contains('widthHeight__value')) {
      var sk = sizeKey(t);
      if (sk) flushCommit(sk, commitSize, t);
      return;
    }
  }, true);

  /* ------------------------------ input ------------------------------ */

  document.addEventListener('input', function (e) {
    var t = e.target;
    if (!t || !t.closest || !t.closest('multi-upload-wrapper')) return;
    var cl = t.classList;
    if (!cl) return;
    if (cl.contains('widthHeight__value')) {
      var sk = sizeKey(t);
      if (sk) scheduleCommit(sk, commitSize, t);
      return;
    }
    if (cl.contains('customQtyFile__qty')) {
      var qk = qtyKeyFor(t);
      if (qk) scheduleCommit(qk, commitQty, t);
      return;
    }
  }, true);
})();
